import { cn } from "@/lib/utils";
import { CheckBox } from "@/components/CheckBox";
import { Checkbox } from "@/components/ui/checkbox";
import { EditLineItem } from "@/components/EditLineItem";
import { InputField } from "@/components/input/InputField";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useXactimateMapping } from "@/hooks/useXactimateMapping";
import { LineItemFeedback } from "@/components/estimation-details/details-section/LineItemFeedback";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useShowDebugItems } from "@/hooks/useShowDebugItems";
import type { LineItem, Tuple } from "@/interfaces/estimate-detail";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useDispatch, useSelector } from "@/redux/store";
import { getKeyForMultiSelect } from "@/utils/utils";
import { capitalizeWords } from "@rebuild/shared";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useLocateFloatingComponent } from "@/util/useLocateFloatingComponent";
import { Trash2, CircleHelp, Flag } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Category } from ".";
import { useLineItemOptions } from "../hooks/useLineItemOptions";
import { Confidence } from "./Confidence";
import EngineSharedUtils from "./util/engine-shared-utils";
import type { FormulaSource, FormulaUnit, IQuantityFormula, ReconcileFormulaUnitResult } from "@rebuild/shared";
import {
  attemptToStripUnits,
  getFormulaErrorMessage,
  getRawFormulaFromFormula,
  reconcileFormulaUnit,
  validateUserTypedFormula,
} from "@rebuild/shared";
import { SimilarIssues } from "./SimilarIssues";
import { getLineItemComponentKey } from "@/util/utils";
import { useCat3LineRowHighlight } from "@/components/estimation-details/cat3-sync/Cat3LineHighlightContext";

interface LineProps {
  order: number | string;
  showCheck?: boolean;
  lineItem: LineItem;
  tuple: Tuple;
  icon?: ReactNode;
  tupleId: string;
  pageId: string;
  confidence?: string;
  className?: string;
  showConfirm?: boolean;
  selectedTab?: Category;
  estimateId?: string;
  quantityString?: string;
  isFreeFormFlag: boolean;
  onSelect?: () => void;
  onClick: (e: React.MouseEvent, lineItemId: string) => void;
  isBeingMoved?: boolean;
  showOrder?: boolean;
  roomName?: string;
  dragHandle?: ReactNode;
}

const convertFreeText = (name: string) => {
  return name === "Free Text Identified: Please Edit!" ? "Unidentified text found: please review" : name;
};

// Turn a reconcile result into the formula to persist: a re-tagged formula for "convert", a cleared
// formula for "drop", or undefined for "keep" (which preserves the existing formula on update).
// A rename only re-units the existing formula, so its provenance carries over — a formula the user
// typed stays user-typed (and keeps showing as entered) after the line item is renamed.
const buildReconciledFormula = (result: ReconcileFormulaUnitResult, source?: FormulaSource): IQuantityFormula | undefined => {
  if (result.action === "convert") {
    return {
      initialValue: 1,
      base: "ea",
      operator: "",
      modifierValue: 0,
      modifierSuffix: "",
      rawFormula: result.rawFormula,
      isMultiUnit: true,
      source,
    };
  }
  if (result.action === "drop") {
    return { initialValue: 0, base: "", operator: "", modifierValue: 0, modifierSuffix: "", rawFormula: "", isMultiUnit: true };
  }
  return undefined;
};

export const Line = ({
  icon,
  order,
  pageId,
  tupleId,
  lineItem,
  tuple,
  onSelect,
  className,
  confidence,
  showConfirm,
  estimateId,
  selectedTab,
  quantityString: _quantityString,
  showCheck = false,
  onClick: onDelete,
  isBeingMoved = false,
  showOrder = true,
  roomName,
  dragHandle,
  ...rest
}: LineProps & HTMLAttributes<HTMLDivElement>) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const showFeedback = isFeatureEnabled("LINE_ITEM_FEEDBACK");
  const allowInvalidFormulas = isFeatureEnabled("ALLOW_USER_TO_SAVE_INVALID_FORMULAS");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { cat, selCode } = useXactimateMapping(lineItem.lineItemId, lineItem.lineItem);
  const [inputError, setInputError] = useState<string>();
  const [editHeight, setEditHeight] = useState<number>(0);
  const [showEditQuantity, setShowEditQuantity] = useState(false);
  const [showEditItemName, setShowEditItemName] = useState(false);
  const [newLineItemStr, setNewLineItemStr] = useState<string>(lineItem.lineItem);

  const dispatch = useDispatch();
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const selectedLineItems = useSelector(estimationSelector.selectedLineItems);
  const isDebugItemsEnabled = useShowDebugItems();

  const { containerRef, position } = useLocateFloatingComponent(editHeight);

  const {
    updateLineItem,
    onConfirm,
    getLineItemElements,
    getAllowedUnitsForLineItem,
    onSearchLineItem,
    lineItemXactimateMapping,
    rateCardUnitByTemplateId,
    addUserXactimateMapping,
    deleteThenUpdateLineItem,
    createLineItem,
    knownLineItemKeys,
    addKnownLineItemKey,
  } = useEstimateDetail(estimateId!);

  const isMultiSelected = (item: { tupleId: string; lineItemId: string; pageId: string; lineItem: string }) =>
    selectedLineItems.some((s) => getKeyForMultiSelect(s) === getKeyForMultiSelect(item));
  const addToSelection = (item: { tupleId: string; lineItemId: string; pageId: string; lineItem: string }) =>
    dispatch(estimationActions.addLineItemToSelection(item));
  const removeFromSelection = (item: { tupleId: string; lineItemId: string; pageId: string; lineItem: string }) =>
    dispatch(estimationActions.removeLineItemFromSelection(item));
  const { onFindOptions, options, onSelectItem, matchedPermutation, itemSearchSelected, reset, setItemSearchSelected } =
    useLineItemOptions(onSearchLineItem);

  const { lineItem: name, lineItemId, uniqueId, qnm, confirmed, formula: itemFormula } = lineItem;

  const isCat3SyncRowHighlight = useCat3LineRowHighlight(uniqueId);

  // Brief green pulse (same as Cat 3 line sync highlight) when this row is first seen after add/modify
  const [wasJustAdded, setWasJustAdded] = useState(false);
  const wasJustAddedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const key = getLineItemComponentKey(pageId, tuple, lineItem);
    if (!knownLineItemKeys.includes(key)) {
      setWasJustAdded(true);
      addKnownLineItemKey(key);

      wasJustAddedTimeoutRef.current = setTimeout(() => {
        setWasJustAdded(false);
        wasJustAddedTimeoutRef.current = null;
      }, 1_500);
    }
  }, [pageId, tuple, lineItem, knownLineItemKeys, addKnownLineItemKey]);

  // Clear timeout only on unmount
  useEffect(() => {
    return () => {
      if (wasJustAddedTimeoutRef.current) {
        clearTimeout(wasJustAddedTimeoutRef.current);
      }
    };
  }, []);

  // Create current line item object for multi-selection
  const currentLineItem = useMemo(
    () => ({
      tupleId,
      lineItemId,
      pageId,
      lineItem: name,
    }),
    [tupleId, lineItemId, pageId, name],
  );

  // Check if this item is in the multi-selection using the hook
  const isItemMultiSelected = isMultiSelected(currentLineItem);

  // Check if this is the currently active line item (selected for sidebar details)
  const isActiveLineItem = lineItemDetail?.lineItem.uniqueId === lineItem.uniqueId;

  const quantity = qnm[0]?.quantity;
  const measurement = qnm[0]?.measurement;

  const allowedUnits = useMemo(
    () => EngineSharedUtils.getAllowedUnitsFromXactimateMapping(lineItem, lineItemXactimateMapping).map((unit) => unit.toUpperCase()),
    [lineItem, lineItemXactimateMapping],
  );
  // T&M line items have no xactimate mapping; their unit lives on the rate item
  // and is keyed by lineItemTemplate.id (which is stored as lineItemId here).
  const rateCardUnit = rateCardUnitByTemplateId?.[lineItem.lineItemId]?.toUpperCase();
  const correctUnit = (allowedUnits[0] ?? rateCardUnit) as FormulaUnit | undefined;

  // string representation of the formula saved in the line item object
  const lineItemFormulaString = useMemo(() => getRawFormulaFromFormula(itemFormula), [itemFormula]);

  // Formulas are stored expanded ("2*10 SF"). Unless the org opted into keeping formulas separate,
  // show the combined form ("20 SF"). Only affects display/editing — lineItemFormulaString (used for
  // reconcile/save comparisons below) stays the actual stored value. A user-typed formula is shown
  // exactly as entered regardless of the flag — the display preference only governs Rebuild-generated ones.
  const keepFormulasExpanded = isFeatureEnabled("KEEP_FORMULAS_EXPANDED");
  const isUserTypedFormula = itemFormula?.source === "user";
  const displayFormulaString = useMemo(() => {
    if (keepFormulasExpanded || isUserTypedFormula || !lineItemFormulaString) return lineItemFormulaString;
    return (
      validateUserTypedFormula(lineItemFormulaString, correctUnit, { keepUnitInRawFormula: true }).rawFormulaFormatted ??
      lineItemFormulaString
    );
  }, [keepFormulasExpanded, isUserTypedFormula, lineItemFormulaString, correctUnit]);

  const [formulaString, setFormulaString] = useState<string | undefined>(displayFormulaString);
  useEffect(() => setFormulaString(displayFormulaString), [displayFormulaString]);

  // Strip the unit from the editable value — it's shown separately as a non-editable suffix.
  // attemptToStripUnits drops unit tokens textually (e.g. "70 SF" -> "70", "W + 5 SF" -> "W + 5").
  const editableFormulaString = useMemo(() => {
    if (!formulaString || !correctUnit) return formulaString;
    return attemptToStripUnits(formulaString, correctUnit) ?? formulaString;
  }, [formulaString, correctUnit]);

  const toggleEditQuantity = useCallback(() => {
    setShowEditQuantity((prev) => !prev);
  }, []);

  const toggleEditItemName = useCallback(() => setShowEditItemName((prev) => !prev), []);

  const onItemClick = useCallback(
    (e: React.MouseEvent<HTMLParagraphElement, MouseEvent>) => {
      e.preventDefault();
      toggleEditItemName();
    },
    [toggleEditItemName],
  );

  // Validate formula string for display purposes (show error message)
  useEffect(() => {
    // An empty quantity isn't allowed — flag it like any other invalid input.
    if (!formulaString?.length) {
      setInputError("Please enter a quantity");
      return;
    }

    const result = validateUserTypedFormula(formulaString, correctUnit);

    if (result.errorMessage) {
      setInputError(getFormulaErrorMessage(result.errorMessage, correctUnit));
      return;
    }

    setInputError(undefined);
  }, [formulaString, correctUnit]);

  const onBlurQuantityInput = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      const inputValue = e.target.value;
      toggleEditQuantity();

      // Empty input isn't persisted (the validate effect flags it); just reflect it in the field.
      if (!inputValue?.trim()) {
        setFormulaString(inputValue);
        return;
      }

      // Persist whatever the user typed; the correct unit is supplied via context, not re-attached here.
      const validation = validateUserTypedFormula(inputValue, correctUnit);
      const newFormulaString = inputValue;
      setFormulaString(newFormulaString);

      // Don't persist if the value is unchanged or invalid.
      if (newFormulaString === editableFormulaString) return;
      if (validation.errorMessage && !allowInvalidFormulas) return;

      const formula: IQuantityFormula = {
        initialValue: 1,
        base: "ea",
        operator: "",
        modifierValue: 0,
        modifierSuffix: "",
        rawFormula: newFormulaString,
        isMultiUnit: true,
        source: "user",
      };

      // Update the line item directly
      updateLineItem(pageId, tupleId, lineItemId, uniqueId, name, quantity ?? 0, measurement, formula);
    },
    [
      lineItemFormulaString,
      correctUnit,
      quantity,
      uniqueId,
      pageId,
      tupleId,
      lineItemId,
      name,
      measurement,
      toggleEditQuantity,
      updateLineItem,
      allowInvalidFormulas,
    ],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onBlurQuantityInput(e as unknown as React.FocusEvent<HTMLInputElement, Element>);
    }
  };

  const onChangeSearch = useCallback(
    (id: string, label: string) => {
      if (label === name) return;
      onSelectItem(id, label);
    },
    [name, onSelectItem],
  );

  const onSaveNewLineItemStr = useCallback(
    async (deleteParams?: { tupleId: string; pageId: string; uniqueId: string; category?: Category }) => {
      toggleEditItemName();
      const newName = newLineItemStr?.trimStart();
      if (newName === name) return;

      // Reconcile the formula's unit to the new permutation up front so the rename persists the
      // corrected formula in a single updateLineItem call (instead of relying on the self-heal
      // effect to fire a second write once the new mapping loads). undefined preserves the formula.
      let formula: IQuantityFormula | undefined = undefined;
      const newLineItemId = itemSearchSelected?.id;
      if (newLineItemId && lineItemFormulaString) {
        const newAllowedUnits = await getAllowedUnitsForLineItem(newName, newLineItemId);
        const result = reconcileFormulaUnit(newLineItemId, newName, lineItemFormulaString, newAllowedUnits);
        if (result.action === "drop") console.warn(`Dropping formula for "${newName}": ${result.dropReason}`);
        formula = buildReconciledFormula(result, itemFormula?.source);
      }

      const updateParams = {
        pageId,
        tupleId,
        lineItemId,
        uniqueId,
        item: newName,
        quantity: quantity ?? 0,
        measurement,
        formula,
        confirmed: undefined,
        newLineItemId,
        orderIndex: itemSearchSelected?.orderIndex,
      };

      if (deleteParams) {
        await deleteThenUpdateLineItem(deleteParams, updateParams);
      } else {
        // Check if this is an issue line item (has error)
        const isIssue = lineItemDetail?.tuple?.hasError || lineItemDetail?.tuple?.isFreeFormFlag;

        await updateLineItem(
          pageId,
          tupleId,
          lineItemId,
          uniqueId,
          newName,
          quantity ?? 0,
          measurement,
          formula,
          undefined,
          newLineItemId,
          itemSearchSelected?.orderIndex,
          isIssue, // Skip order index update if this is an issue
        );
      }
    },
    [
      uniqueId,
      deleteThenUpdateLineItem,
      getAllowedUnitsForLineItem,
      lineItemFormulaString,
      itemSearchSelected?.id,
      itemSearchSelected?.orderIndex,
      lineItemDetail?.tuple?.hasError,
      lineItemDetail?.tuple?.isFreeFormFlag,
      lineItemId,
      measurement,
      name,
      newLineItemStr,
      pageId,
      quantity,
      toggleEditItemName,
      tupleId,
      updateLineItem,
    ],
  );

  const onClickConfirm = useCallback(() => {
    onConfirm?.(pageId, tupleId, lineItemId, selectedTab);
  }, [lineItemId, onConfirm, pageId, selectedTab, tupleId]);

  const onCheck = useCallback(() => {
    updateLineItem(pageId, tupleId, lineItemId, uniqueId, name, quantity ?? 0, measurement, undefined, !confirmed);
  }, [confirmed, lineItemId, measurement, name, uniqueId, pageId, quantity, tupleId, updateLineItem]);

  const onHeightChange = useCallback((height: number) => {
    setEditHeight(height);
  }, []);

  // Handle line item click — only opens sidebar details, does NOT toggle checkbox selection
  const handleLineItemClick = useCallback(() => {
    onSelect?.();
  }, [onSelect]);

  const percentage = !formulaString ? 10 : confidence?.split("%")[0];

  return (
    <div className="flex flex-col">
      <div
        className={`group grid h-[52px] cursor-pointer grid-cols-9 items-center border-[#EEEFF1] px-4 py-2 ${className} ${isBeingMoved && "!bg-slate-50"} ${(isItemMultiSelected || isActiveLineItem) && "!bg-gray-100"} ${wasJustAdded || isCat3SyncRowHighlight ? "animate-cat3-line-highlight-fade" : ""}`}
        onClick={handleLineItemClick}
        {...rest}
      >
        <div ref={containerRef} className="relative col-span-4 flex w-full items-center">
          <div className="flex flex-row items-center truncate">
            {dragHandle}
            <Checkbox
              checked={isItemMultiSelected}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={() => {
                if (isItemMultiSelected) {
                  removeFromSelection(currentLineItem);
                } else {
                  addToSelection(currentLineItem);
                }
              }}
              className="mr-2"
            />
            {showOrder && <p className="mr-2 rounded-md text-sm font-medium">{order}.</p>}
            {icon}
            {
              <p
                className={`truncate rounded-md px-2 py-1 text-sm font-medium transition-colors duration-200 hover:bg-[#F4F4F5]`}
                onClick={onItemClick}
              >
                {roomName ? <span>{capitalizeWords(roomName)} - </span> : null}
                {capitalizeWords(convertFreeText(name))}
              </p>
            }
            {/* AI Reasoning Tooltip - only shown when debug flag is enabled and _aiReasoning exists */}
            {isDebugItemsEnabled && tuple._aiReasoning && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="ml-2 rounded-full p-1 transition-colors duration-200 hover:bg-gray-100">
                    <CircleHelp className="h-4 w-4 text-gray-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-xs">
                  <p className="text-xs">{tuple._aiReasoning}</p>
                  {tuple._improvement ? (
                    <pre className="mt-2 max-h-40 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-800">
                      {JSON.stringify(tuple._improvement, null, 2)}
                    </pre>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {showEditItemName && (
            <EditLineItem
              reset={reset}
              options={options}
              position={position}
              lineItemText={name}
              itemId={lineItemId}
              toggle={toggleEditItemName}
              onSave={onSaveNewLineItemStr}
              onFindOptions={onFindOptions}
              onChangeSearch={onChangeSearch}
              matchedPermutation={matchedPermutation}
              onHeightChange={onHeightChange}
              itemSearchSelected={itemSearchSelected}
              getLineItemElements={getLineItemElements}
              previousLineItem={name}
              createLineItem={async (secondLinePermutation: string) => {
                await createLineItem(pageId, secondLinePermutation, lineItemId);
              }}
              onChange={(value) => {
                setNewLineItemStr(value);
              }}
              onCreateLineItem={async (mapping) => {
                const response = await addUserXactimateMapping(mapping);
                if (!response.success || !response.lineItem) {
                  return false;
                }
                setItemSearchSelected(response.lineItem);
                return true;
              }}
              pageId={pageId}
              estimateId={estimateId}
            />
          )}
        </div>

        <div className="col-span-3 flex w-[100%] flex-row items-center justify-end px-8">
          {!showEditQuantity ? (
            <div
              onClick={() => {
                toggleEditQuantity();
              }}
              className="flex min-w-0 cursor-pointer items-center justify-end gap-2 p-2"
            >
              {inputError && <p className="text-center text-xs !text-orange-500">{`${inputError}`}</p>}
              {formulaString ? (
                <>
                  {/* Cap width so long formulas can't push the row across the screen; the right-edge
                      fade only shows once the text overflows past the trailing padding. */}
                  <div className="max-w-64 overflow-hidden [mask-image:linear-gradient(to_right,black_calc(100%_-_1rem),transparent)] pr-2.5">
                    <p className="whitespace-nowrap">{editableFormulaString}</p>
                  </div>
                  <p className="w-10 shrink-0 text-left text-gray-400">{correctUnit}</p>
                </>
              ) : (
                <p className="shrink-0 rounded-2xl bg-red-50 pr-2 pl-2 text-red-500">N/A</p>
              )}
            </div>
          ) : (
            <div className="flex w-full justify-end">
              <InputField
                autoFocus={true}
                defaultValue={editableFormulaString}
                className="justify-center"
                inputClassName="h-6 text-xs mx-auto"
                onBlur={onBlurQuantityInput}
                name={"measurement"}
                centerError={true}
                onKeyDown={onKeyDown}
                unitSuffix={correctUnit}
              />
            </div>
          )}
        </div>

        <div className="col-span-1">
          <Confidence percentage={percentage ? +percentage : 0} />
        </div>

        <div className="col-span-1 flex flex-row items-center justify-end space-x-2">
          <div
            className={cn(
              "flex flex-row items-center justify-end space-x-2 transition-opacity duration-200",
              feedbackOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100",
            )}
          >
            {showFeedback && (
              <Popover open={feedbackOpen} onOpenChange={setFeedbackOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Flag className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <LineItemFeedback
                    estimateId={estimateId ?? ""}
                    lineItem={lineItem}
                    roomName={roomName || tuple?.pageMetadata?.roomName || "General"}
                    cat={cat}
                    selCode={selCode}
                    onClose={() => setFeedbackOpen(false)}
                  />
                </PopoverContent>
              </Popover>
            )}
            {showConfirm && (
              <Button
                onClick={onClickConfirm}
                variant="ghost"
                size="sm"
                disabled={!!inputError || !formulaString || lineItem.lineItem === "Free Text Identified: Please Edit!"}
              >
                Confirm
              </Button>
            )}
            <Button
              variant="destructive"
              size="icon-sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(e, lineItemId);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {lineItem.comment && <img alt="comment" src="/comment.svg" className="h-5 w-5" />}
          {showCheck && <CheckBox checked={confirmed ?? false} onChange={onCheck} />}
        </div>
      </div>

      <div className="col-span-1 flex">
        {selectedTab === "issues" && <SimilarIssues estimateId={estimateId!} pageId={pageId} lineItem={lineItem} tupleId={tupleId} />}
      </div>
    </div>
  );
};
